import * as THREE from "three";
import { splice } from "../shader";
import { NEUTRAL_BUILD, type ArtStyle } from "./types";

// Cartoon: "plastic toy" (#39). Smooth physically based shading (soft
// gradients and a gentle specular) plus a rim light that picks out
// silhouettes, thin ink outlines, a studio environment and neutral tone
// mapping that keeps the colors saturated. The default look.

/** Rim light strength and falloff: a soft glow on edges facing away from the camera. */
const RIM = 0.28;
const RIM_POWER = 3.0;

function addRim(mat: THREE.MeshStandardMaterial): void {
  mat.onBeforeCompile = (shader) => {
    shader.fragmentShader = splice(
      shader.fragmentShader,
      "#include <opaque_fragment>",
      `{
        float rimF = 1.0 - saturate(dot(normalize(vViewPosition), normal));
        outgoingLight += mix(vec3(1.0), diffuseColor.rgb, 0.4) * pow(rimF, ${RIM_POWER.toFixed(1)}) * ${RIM.toFixed(2)};
      }`,
      "before",
    );
  };
  mat.customProgramCacheKey = () => "toy-rim";
}

export const cartoon: ArtStyle = {
  id: "cartoon",
  name: "Cartoon",
  pitch: "Plastic toy: smooth glossy shapes, soft rim light, thin ink outlines.",
  cost: "low",

  // Every surface is the same smooth, slightly glossy plastic; the call site's
  // roughness, metalness and maps vary it.
  material(color, params) {
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.55, metalness: 0, ...params });
    addRim(mat);
    return mat;
  },

  outlines: { scale: 0.55, ink: 0x2a2238, edges: 0.28 },

  lighting: {
    sunColor: 0xffffff,
    sun: 1,
    hemi: 1,
    environment: "studio",
    envIntensity: { outdoor: 0.4, indoor: 0.3 },
  },

  sky: { clouds: true },
  fog: { near: 1, far: 1 },

  // Neutral tone mapping keeps the cartoon colors saturated while rolling
  // off highlights softly.
  toneMapping: THREE.NeutralToneMapping,
  exposure: 0.95,

  // No post-processing: the cartoon look renders straight to the canvas.
  post: { ao: null, bloom: null, grade: null, tiltShift: null, mood: null, dof: null },

  dust: null,

  resolution: null,

  // The chunky, big-headed toy rider.
  rider: NEUTRAL_BUILD,

  shadows: { mapSize: 2048, radius: 1, bias: -0.0005, normalBias: 0.04 },
};
